import React from 'react';
import {
  Badge, Button, ButtonGroup, Col, Form, Row,
} from 'react-bootstrap';
import { countActiveFilters } from './InvoiceFilters';

export const MISSING_KIND_LABELS = {
  no_documents: 'No documents',
  no_szamla: 'No final Számla',
};

export const AGE_LABELS = {
  '0-7': 'Last 7 days',
  '8-30': '8–30 days ago',
  '31-60': '31–60 days ago',
  '60+': 'Over 60 days ago',
};

export const EMPTY_MISSING_FILTERS = {
  q: '',
  status: '',
  age: '',
  partner_id: '',
  event_name: '',
  from: '',
  to: '',
  hidden: false,
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Confirmed', label: 'Confirmed' },
];

export const partnerLabel = (id, facets = {}) => (
  (facets.partners || []).find((p) => p.id === id)?.name || id
);

// The filters that are not already visible as their own control, shown as
// removable chips — including the ones set by clicking a chart bar.
const chipsFor = (filters, facets) => [
  filters.age && { key: 'age', label: `Trip ${AGE_LABELS[filters.age] || filters.age}` },
  filters.partner_id && { key: 'partner_id', label: partnerLabel(filters.partner_id, facets) },
  filters.event_name && { key: 'event_name', label: `Event ${filters.event_name}` },
  filters.from && { key: 'from', label: `Trips from ${filters.from}` },
  filters.to && { key: 'to', label: `Trips to ${filters.to}` },
].filter(Boolean);

/**
 * Filter bar for the Missing documents backlog. Every control maps to a
 * server-side filter; partner and trip age are also set by clicking the charts,
 * and every active filter is a chip that removes itself.
 */
function MissingDocumentsFilters({
  filters,
  onChange,
  facets = { partners: [], events: [] },
  open,
  onToggle,
}) {
  const set = (field) => (e) => onChange({ ...filters, [field]: e.target.value });
  const chips = chipsFor(filters, facets);
  const active = countActiveFilters({ ...filters, status: '', hidden: false });

  return (
    <div className="mb-3">
      <Row className="g-2 align-items-center">
        <Col xs={12} md={4}>
          <Form.Control
            size="sm"
            type="search"
            placeholder="Search reference, customer or document…"
            aria-label="Search missing documents"
            value={filters.q}
            onChange={set('q')}
          />
        </Col>
        <Col xs={12} md="auto">
          <ButtonGroup size="sm" aria-label="Booking status">
            {STATUS_OPTIONS.map((o) => (
              <Button
                key={o.value || 'all'}
                variant={filters.status === o.value ? 'primary' : 'outline-secondary'}
                aria-pressed={filters.status === o.value}
                onClick={() => onChange({ ...filters, status: o.value })}
              >
                {o.label}
              </Button>
            ))}
          </ButtonGroup>
        </Col>
        {/* The Hidden view is switched by its KPI tile ("Hidden by the
            team"), which also shows how many there are. */}
        <Col xs="auto" className="ms-md-auto d-flex align-items-center gap-3">
          <Button size="sm" variant={open ? 'secondary' : 'outline-secondary'} onClick={onToggle}>
            <i className="fa fa-sliders me-1" />
            More
            {active > 0 && <Badge bg="primary" className="ms-1">{active}</Badge>}
          </Button>
        </Col>
      </Row>

      {open && (
        <Row className="g-2 mt-1 p-2 border rounded bg-light-subtle">
          <Form.Group as={Col} xs={6} md={3} controlId="missing-filter-from">
            <Form.Label className="small text-muted mb-1">Trip from</Form.Label>
            <Form.Control size="sm" type="date" value={filters.from} onChange={set('from')} />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="missing-filter-to">
            <Form.Label className="small text-muted mb-1">Trip to</Form.Label>
            <Form.Control size="sm" type="date" value={filters.to} onChange={set('to')} />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="missing-filter-partner">
            <Form.Label className="small text-muted mb-1">Partner</Form.Label>
            <Form.Select size="sm" value={filters.partner_id} onChange={set('partner_id')}>
              <option value="">Any</option>
              {(facets.partners || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="missing-filter-event">
            <Form.Label className="small text-muted mb-1">Event</Form.Label>
            <Form.Select size="sm" value={filters.event_name} onChange={set('event_name')}>
              <option value="">Any</option>
              {(facets.events || []).map((e) => <option key={e} value={e}>{e}</option>)}
            </Form.Select>
          </Form.Group>
        </Row>
      )}

      {(chips.length > 0 || filters.q) && (
        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
          {chips.map((chip) => (
            <Button
              key={chip.key}
              size="sm"
              variant="light"
              className="filter-chip"
              onClick={() => onChange({ ...filters, [chip.key]: '' })}
              aria-label={`Remove filter ${chip.label}`}
            >
              {chip.label}
              <i className="fa fa-times ms-2" aria-hidden="true" />
            </Button>
          ))}
          <Button
            size="sm"
            variant="link"
            className="text-muted p-0"
            onClick={() => onChange({ ...EMPTY_MISSING_FILTERS, hidden: filters.hidden })}
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
}

export default MissingDocumentsFilters;
